import { useEffect, useMemo, useRef } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import NotFoundPage from './pages/NotFoundPage';
import { homeSubContext, isTagItReady, trackTagItEvent } from './lib/tagitTrack';

function subContextForLocation(location: { pathname: string; hash: string }) {
  if (location.pathname === '/') {
    return homeSubContext(location.hash);
  }
  if (location.pathname === '/blog') return 'blog';
  if (location.pathname.startsWith('/blog/')) {
    const slug = location.pathname.slice('/blog/'.length).split('/')[0].trim();
    return slug ? `blog:${slug}` : 'blog';
  }
  return 'site';
}

function TagItBootstrap() {
  const location = useLocation();
  const subContext = useMemo(() => subContextForLocation(location), [location.pathname, location.hash]);
  const pendingSubContextRef = useRef<string | null>(null);
  const pageViewSentRef = useRef(false);

  useEffect(() => {
    const tagId = import.meta.env.VITE_TAGIT_TAG_ID as string | undefined;
    const initiatorUrl =
      (import.meta.env.VITE_TAGIT_INITIATOR_URL as string | undefined) ||
      'https://storage.googleapis.com/tagitsdk/initiator.js';
    const sdkCollectorUrl =
      (import.meta.env.VITE_TAGIT_SDK_COLLECTOR_URL as string | undefined) ||
      'https://us-central1-tagit-581e0.cloudfunctions.net/sdk-collector';
    const debugMode = (import.meta.env.VITE_TAGIT_DEBUG_MODE as string | undefined) === 'true';

    if (!tagId) {
      if (import.meta.env.DEV) {
        console.warn('[tagIt] VITE_TAGIT_TAG_ID not set; SDK will not initialize.');
      }
      return;
    }

    // Configure the SDK before loading initiator.js (per Web SDK integration guide).
    window.tagItConfig = {
      tagId,
      sdkURL: sdkCollectorUrl,
      eventSubContext: subContext,
      data: [
        {
          eventSubContext: subContext,
          entityData: { identifiers: [] },
          privacy: {},
        },
      ],
      features: [
        {
          collectDeviceData: true,
          debugMode,
          doNotCollectPurposes: {},
          enableKetch: false,
        },
      ],
    };

    // Inject initiator if needed (idempotent across HMR / StrictMode).
    const existing = document.querySelector<HTMLScriptElement>('script[data-tagit-initiator="true"]');
    if (!existing) {
      const script = document.createElement('script');
      script.src = initiatorUrl;
      script.async = true;
      script.dataset.tagitInitiator = 'true';
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (pageViewSentRef.current) {
      return;
    }

    const fire = () => {
      if (pageViewSentRef.current) {
        return;
      }
      pageViewSentRef.current = true;
      const desired = pendingSubContextRef.current || subContext;
      pendingSubContextRef.current = null;
      try {
        trackTagItEvent({
          eventType: 'view',
          eventSubType: 'page',
          eventSubContext: desired,
        });
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[tagIt] trackEvent failed', err);
        }
      }
    };

    if (isTagItReady()) {
      fire();
      return;
    }

    pendingSubContextRef.current = subContext;
    document.addEventListener('tagItSDKReady', fire, { once: true });

    const fallbackTimer = window.setTimeout(() => {
      if (isTagItReady()) fire();
    }, 1500);

    return () => {
      document.removeEventListener('tagItSDKReady', fire);
      window.clearTimeout(fallbackTimer);
    };
  }, [subContext]);

  return null;
}

function App() {
  return (
    <BrowserRouter>
      <TagItBootstrap />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
